import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useRef, useState } from 'react'

import type { InlineImageField, PendingInlineImage } from './useArticleUploads'
import { articleApi } from '../services/articleApi'
import type { ArticleAttachment, FileUploadStatus } from '../types/article.types'
import type { ArticleFormState } from '../types/article-form.types'
import { getFileSignature, mergeUniqueFiles, renameFile } from '../utils/fileUtils'
import { extractRichTextImageSources } from '../utils/richText'

const INLINE_IMAGE_FIELD_LABELS: Record<InlineImageField, string> = {
  problem: 'Problem / Background',
  solution: 'Solution / Procedure',
}

type UseArticleInlineImagesOptions = {
  articleId?: string
  existingAttachments: ArticleAttachment[]
  form: ArticleFormState
  images: File[]
  setForm: Dispatch<SetStateAction<ArticleFormState>>
  setImages: Dispatch<SetStateAction<File[]>>
  setUploadStatuses: Dispatch<SetStateAction<Record<string, FileUploadStatus>>>
}

function isInlineImageReferenced(content: string, temporarySource: string) {
  return content.includes(temporarySource)
}

export function useArticleInlineImages({
  articleId,
  existingAttachments,
  form,
  images,
  setForm,
  setImages,
  setUploadStatuses,
}: UseArticleInlineImagesOptions) {
  const [pendingInlineImages, setPendingInlineImages] = useState<PendingInlineImage[]>([])
  const pendingInlineImagesRef = useRef<PendingInlineImage[]>([])

  useEffect(() => {
    pendingInlineImagesRef.current = pendingInlineImages
  }, [pendingInlineImages])

  useEffect(() => () => {
    pendingInlineImagesRef.current.forEach((image) => {
      URL.revokeObjectURL(image.temporarySource)
    })
  }, [])

  function syncInlineImages(problem: string, solution: string) {
    const previousInlineImages = pendingInlineImagesRef.current
    const activeInlineImages = previousInlineImages.filter((image) =>
      isInlineImageReferenced(problem, image.temporarySource) ||
      isInlineImageReferenced(solution, image.temporarySource),
    )

    if (activeInlineImages.length === previousInlineImages.length) {
      return
    }

    const activeSignatures = new Set(activeInlineImages.map((image) => getFileSignature(image.file)))

    previousInlineImages
      .filter((image) => !activeSignatures.has(getFileSignature(image.file)))
      .forEach((image) => {
        URL.revokeObjectURL(image.temporarySource)
      })

    pendingInlineImagesRef.current = activeInlineImages
    setPendingInlineImages(activeInlineImages)
    setImages((current) =>
      current.filter(
        (image) =>
          activeSignatures.has(getFileSignature(image)) ||
          !previousInlineImages.some(
            (pendingImage) => getFileSignature(pendingImage.file) === getFileSignature(image),
          ),
      ),
    )
  }

  function setRichTextField(field: InlineImageField) {
    return (value: string) => {
      setForm((current) => {
        const nextForm = { ...current, [field]: value }
        syncInlineImages(nextForm.problem, nextForm.solution)
        return nextForm
      })
    }
  }

  function buildInlineImageLabels() {
    const labels = new Map<string, string>()
    const pendingBySource = new Map(pendingInlineImages.map((image) => [image.temporarySource, image]))

    ;([
      ['problem', form.problem],
      ['solution', form.solution],
    ] as const).forEach(([field, content]) => {
      extractRichTextImageSources(content).forEach((source, index) => {
        const image = pendingBySource.get(source)
        if (!image) {
          return
        }

        labels.set(
          getFileSignature(image.file),
          `Linked in ${INLINE_IMAGE_FIELD_LABELS[field]} #${index + 1}`,
        )
      })
    })

    return labels
  }

  function buildExistingInlineImageLabels() {
    const labels = new Map<number, string>()
    if (!articleId) {
      return labels
    }

    const existingImages = existingAttachments.filter((attachment) => attachment.mime_type.startsWith('image/'))

    ;([
      ['problem', form.problem],
      ['solution', form.solution],
    ] as const).forEach(([field, content]) => {
      extractRichTextImageSources(content).forEach((source, index) => {
        const linkedAttachment = existingImages.find((attachment) => {
          const attachmentUrl = articleApi.publicImageUrl(articleId, attachment.id)
          return source === attachmentUrl || source.includes(`/articles/public/${articleId}/attachments/${attachment.id}`)
        })

        if (!linkedAttachment) {
          return
        }

        labels.set(linkedAttachment.id, `Linked in ${INLINE_IMAGE_FIELD_LABELS[field]} #${index + 1}`)
      })
    })

    return labels
  }

  function renameLinkedInlineImage(fileSignature: string, nextName: string) {
    const trimmedName = nextName.trim()
    if (!trimmedName) {
      return
    }

    setImages((current) =>
      current.map((image) =>
        getFileSignature(image) === fileSignature ? renameFile(image, trimmedName) : image,
      ),
    )

    setPendingInlineImages((current) =>
      current.map((image) =>
        getFileSignature(image.file) === fileSignature
          ? { ...image, file: renameFile(image.file, trimmedName) }
          : image,
      ),
    )

    setUploadStatuses((current) => {
      const renamedImage = images.find((image) => getFileSignature(image) === fileSignature)
      if (!renamedImage || !current[fileSignature]) {
        return current
      }

      const nextFile = renameFile(renamedImage, trimmedName)
      const next = { ...current }
      next[getFileSignature(nextFile)] = current[fileSignature]
      delete next[fileSignature]
      return next
    })
  }

  async function handleInlineImagePaste(field: InlineImageField, file: File) {
    const temporarySource = URL.createObjectURL(file)

    setImages((current) => mergeUniqueFiles(current, [file]))
    setPendingInlineImages((current) => [
      ...current.filter((image) => image.temporarySource !== temporarySource),
      { field, file, temporarySource },
    ])

    return temporarySource
  }

  function clearPendingInlineImages() {
    pendingInlineImages.forEach((image) => {
      URL.revokeObjectURL(image.temporarySource)
    })
    pendingInlineImagesRef.current = []
    setPendingInlineImages([])
  }

  return {
    clearPendingInlineImages,
    existingInlineImageLabels: buildExistingInlineImageLabels(),
    handleInlineImagePaste,
    inlineLinkedImageLabels: buildInlineImageLabels(),
    pendingInlineImages,
    renameLinkedInlineImage,
    setRichTextField,
  }
}
